// @ts-strict-ignore
import { ElementRef, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LMGlobalPersonalizationService } from '@states/services';
import { cloneDeep } from 'lodash-es';
import { LMContextService, LMLoggerService, LMUrlUtilService } from '@app/services';
import { LMApplicationMenuBaseDirective } from '@components/applicationmenu/lm-application-menu-base.directive';

@Injectable()
export class LMApplicationMenuService {
  constructor(
    private http: HttpClient,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmLoggerService: LMLoggerService,
    private lmContextService: LMContextService,
    private lmUrlUtilService: LMUrlUtilService
  ) {}

  public lastOpenedMenuId: string;
  public lastOpenedMenu: ElementRef;

  private applicationMenuSpec: Array<any> = [];
  private expandedMenus: Array<string> = [];
  public addExpandedMenus(index: string): void {
    this.expandedMenus = [];

    const indexArray = index.split( '.');
    if (indexArray.length === 1) {
      this.expandedMenus.push(index);
    } else {
      let tempIndex = '';
      for (let i = 0; i < indexArray.length; i++) {
        if (i === 0) {
          tempIndex += indexArray[i];
        } else {
          tempIndex += '.' + indexArray[i];
        }
        this.expandedMenus.push(tempIndex);
      }
    }

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }
  /**
   * Keep track of expanded menus for pagestate
   * @param menuItem: LMMenuItemSpec
   */
  public saveExpandedMenus(menuItem: LMMenuItemSpec): void {
    this.updateApplicationMenuState(this.getApplicationMenuSpec(), menuItem);

    this.expandedMenus = [];
    this.saveApplicationMenuState(this.getApplicationMenuSpec());

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  /**
   * Used to clear when roles change
   */
  public clearExpandedMenus() {
    this.expandedMenus = [];

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  public hasExpandedMenus(): boolean {
    return !!this.lmGlobalPersonalizationService.getGlobalPersonalization().expandedMenus;
  }

  public getExpandedMenus(): Array<string> {
    return this.lmGlobalPersonalizationService.getGlobalPersonalization().expandedMenus;
  }

  public saveLastMenuItemClicked(menuIndex: string) {
    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.lastClickedMenuItem = menuIndex;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  public getLastMenuItemClicked() {
    return this.lmGlobalPersonalizationService.getGlobalPersonalization().lastClickedMenuItem;
  }

  public updateApplicationMenuSpec(ApplicationMenuSpec: Array<any>, key?: string, menu?: Array<any>): void {
    if (this.applicationMenuSpec.length === 0) {
      this.applicationMenuSpec = ApplicationMenuSpec;
    } else {
      const newMenu: Array<any> = ApplicationMenuSpec;
      this._updateApplicationMenuSpec(this.applicationMenuSpec, newMenu, key);
    }
  }

  public getApplicationMenuSpec(): Array<any> {
    return this.applicationMenuSpec;
  }

  public setApplicationMenuSpec(newMenuSpec: Array<any>) {
    this.applicationMenuSpec = newMenuSpec;
  }

  public syncWebappMenu(url: string, applicationMenuComponent: LMApplicationMenuBaseDirective) {
    const urlWebAppName = this.lmUrlUtilService.getWebAppNameFromUrl(url);
    if (urlWebAppName !== this.lmContextService.getWebApp()) {
      this.lmLoggerService.log(
        `webApp change detected: new webApp: ${urlWebAppName}, old webapp: ${this.lmContextService.getWebApp()}`);

      // now switch the menu to the future webapp menu if it is different than the current urls
      const webApps: LMWebAppSpec[] = this.lmContextService.getAccessibleWebApps();
      const urlWebAppSpec = webApps ? webApps.filter(webAppSpec => webAppSpec.webAppName === urlWebAppName) : [];
      if (urlWebAppSpec && urlWebAppSpec.length === 1) {
        this.updateWebappMenu(urlWebAppName, applicationMenuComponent);
      }
    }
  }

  public updateWebappMenu(webAppName: string, applicationMenuComponent: LMApplicationMenuBaseDirective) {
    const accessableWebApps = this.lmContextService.getAccessibleWebApps();
    const urlWebAppSpec: LMWebAppSpec[] = accessableWebApps ? accessableWebApps.filter(webAppSpec => webAppSpec.webAppName === webAppName) : [];
    if (urlWebAppSpec.length === 1) {
      this.lmContextService.updateWebApp(urlWebAppSpec[0]);
      if (applicationMenuComponent) {
        applicationMenuComponent.getApplicationMenuPersonalization();
        applicationMenuComponent.loadMenu(this.lmUrlUtilService.getBannerSpecUrl(), true);
      }
    }
  }

  /**
   * Keep track of menu expanded/selected state
   * Later saved to state so accordions can be manually initialized and toggled
   * @param menuSpec
   * @param menuItem
   */
  private updateApplicationMenuState(menuSpecs: Array<LMMenuItemSpec>, menuItem: LMMenuItemSpec) {
    menuSpecs.forEach((ms: LMMenuItemSpec) => {
      if (menuItem.parentIds && menuItem.parentIds.includes(ms.menuIndex)) {
        ms.childExpanded = true;
        menuItem.parentExpanded = true;
      }

      if (menuItem.parentIds && menuItem.parentIds.length === 1 && menuItem.parentIds[0] === '0') {
        menuItem.isRoot = true;
      }

      // Reset menu if parent and child is not expanded
      if ((menuItem.menuIndex !== ms.menuIndex && ms.expanded) && (!menuItem.parentExpanded && !ms.childExpanded)
      ) {
        if (!menuItem.isRoot) {
          ms.menu = ms.menu ? [] : undefined;
        }

        ms.expanded = false;
        ms.childExpanded = false;
        ms.parentExpanded = false;
      }

      // Collapse if at the same level and not the currently expanded menuItem
      if ((ms.parentIds && menuItem.parentIds && ms.parentIds[ms.parentIds.length - 1] === menuItem.parentIds[menuItem.parentIds.length - 1])
        && ms.menuIndex !== menuItem.menuIndex
      ) {
        if (ms.menu && ms.menu.length > 0 && !menuItem.isRoot) {
          ms.menu = [];
        }
        ms.expanded = false;
        ms.childExpanded = false;
        ms.parentExpanded = false;
      }

      if (ms.menu && ms.menu.length > 0) {
        this.updateApplicationMenuState(ms.menu, menuItem);
      }
    });
  }

  private saveApplicationMenuState(menuSpec): void {
    menuSpec.forEach((menuSpecItem: LMMenuItemSpec) => {
      if (menuSpecItem.expanded) {
        this.expandedMenus.push(menuSpecItem.menuIndex);
      }

      if (menuSpecItem.menu && menuSpecItem.menu.length > 0) {
        this.saveApplicationMenuState(menuSpecItem.menu);
      }
    });
  }

  private _updateApplicationMenuSpec(menu: Array<any>, newMenu: Array<any>, key: string) {
    for (let i = 0, l = menu.length; i < l; i++) {
      const object = menu[ i ];
      for (const k in object) {
        if (object.hasOwnProperty(k)) {
          if (object[ k ] === key) {
            object.submenu = newMenu;
          } else if (object.submenu) {
            this._updateApplicationMenuSpec(object.submenu, newMenu, key);
          }
        }
      }
    }
  }

  public getLastOpenedMenuId(): string {
    return this.lastOpenedMenuId;
  }

  public setLastOpenedMenuId(menu: string) {
    this.lastOpenedMenuId = menu;
  }

  public getLastOpenedMenu(): ElementRef {
    return this.lastOpenedMenu;
  }

  public setLastOpenedMenu(menu: ElementRef) {
    this.lastOpenedMenu = menu;
  }

  public resetLastOpenedMenu() {
    this.lastOpenedMenu = null;
    this.lastOpenedMenuId = '';
  }

  public getChangePasswordAction(actions: LMActionSpec[], name: string): LMActionSpec {
    let action: LMActionSpec = null;

    if (!name) { // safety
      return action;
    }

    actions.forEach((a) => {
      if (a.name === name) {
        action = a;
      }
    });

    return action;
  }

  public updateActorContext(applicationMenuUrl: string, userContextProperties: {}): Observable<any>  {
    const postContent: any = {
      requestType:    'updateActorContext',
      contextProperties: userContextProperties
    };

    return this.http.post(applicationMenuUrl, postContent).pipe(
      catchError(error => throwError(error || 'Server error')));
  }

}
